/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from "react";
import { css } from "@emotion/react";
import {
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
  Select,
  Input,
  Icon,
} from "@coreai/component-library";
export interface SelectItemProps {
  textValue: string;
  value: string | number;
}

export interface TablePaginationProps {
  rowsPerPageOptions?: SelectItemProps[];
  totalRows: number;
  defaultPage?: number;
  defaultRowsPerPage?: number;
  navLabel?: string;
  selectId?: string;
  selectLabel?: string;
  inputId?: string;
  inputAriaLabel?: string;
  ofText?: string;
  pageText?: string;
  nextPageLabel?: string;
  previousPageLabel?: string;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  setPageNo?: (page: number) => void;
}
// Styled CSS using Emotion
const tableStyleCss = css`
  display: flex;  
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const TablePagination: React.FC<TablePaginationProps> = ({
  rowsPerPageOptions = [
    { textValue: "5", value: 5 },
    { textValue: "10", value: 10 },
    { textValue: "20", value: 20 },
    { textValue: "50", value: 50 },
  ],
  totalRows,
  defaultPage = 1,
  defaultRowsPerPage,
  navLabel = "Pagination",  
  selectLabel = "Rows per page",
  ofText = "of",
  nextPageLabel = "Next",
  previousPageLabel = "Previous",
  inputId = "pageInputId",
  selectId = "dropdown-content",
  onPageChange,
  onRowsPerPageChange,
  setPageNo,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    defaultRowsPerPage || (rowsPerPageOptions[0].value as number)
  );
  const [currentPage, setCurrentPage] = useState<number>(defaultPage);

  useEffect(() => {
    setCurrentPage(defaultPage);
  }, [defaultPage]);
  useEffect(() => {
    setRowsPerPage(
      defaultRowsPerPage || (rowsPerPageOptions[0].value as number)
    );
  }, [defaultRowsPerPage]);

  const totalPages = Math.ceil(totalRows / rowsPerPage);

  useEffect(() => {
    let checkPage = defaultPage > totalPages ? totalPages : defaultPage;
    if (checkPage) {
      setPageNo && setPageNo(checkPage);
      onPageChange(checkPage);
      setCurrentPage(checkPage);
    }
  }, [totalRows, defaultPage]);

  const handlePageChange = (event: React.ChangeEvent<HTMLInputElement>, totalPages: number) => {
    if (event.target.value?.trim() !== '' && !isNaN(parseInt(event.target.value, ))) {
      let newPage = parseInt(event.target.value, totalPages) || 1;
      newPage = Math.max(1, Math.min(newPage, totalPages));
      setCurrentPage(newPage);
      onPageChange(newPage);
    } else if (event.target.value?.trim() === '') {
      setCurrentPage(0);
    }
  };

  function handleRowsPerPageChange(pageNo: string) {
    const newRowsPerPage = parseInt(pageNo, 10);
    setRowsPerPage(newRowsPerPage);
    onRowsPerPageChange?.(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when rows per page change
    onPageChange(1);
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => {
        const newPage = prev - 1;
        onPageChange(newPage);
        return newPage;
      });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => {
        const newPage = prev + 1;
        onPageChange(newPage);
        return newPage;
      });
    }
  };

  return (
    <div role="region" aria-label={navLabel} css={tableStyleCss}>
      <div style={{ display: "flex", gap: "10px" }}>
        {/* Rows per Page Select */}
        <label
          style={{ marginTop: "10px", fontWeight: 500 }}
          aria-controls={selectId}
        >
          {selectLabel}:
        </label>
        <div id="dropdown-content" style={{ display: "inline" }}>
          <Select
            value={rowsPerPage.toString()}
            onValueChange={handleRowsPerPageChange}    
            // id={selectId}        
          >
            <SelectTrigger
              style={{
                width: "100px",
                margin: "10px",
                paddingTop: "0px",
                paddingBottom: "0px",
              }}
              aria-controls="dropdown-content"
            >
              <SelectValue placeholder="5"  />
            </SelectTrigger>
            <SelectContent id="pages">
              {rowsPerPageOptions.map((option) => (
                <SelectItem
                  value={option.value.toString()}
                  id={option.textValue}
                >
                  {" "}
                  {option.textValue}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Pagination Controls */}
      <div
        className="pagination-controls"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          whiteSpace: "nowrap",
        }}
      >
        {/* Previous Button (Hidden if Disabled) */}
        <button
          onClick={goToPreviousPage}
          style={{
            visibility: currentPage === 1 || currentPage === 0 ? "hidden" : "visible",
            display: "flex",
            fontWeight: 600,
          }}
          aria-controls="dropdown-content" 
          aria-expanded="false" 
          role="combobox"
        >
          <Icon
            style={{ verticalAlign: "textBottom" }}
            className=""
            counterLabel="Messages"
            counterValue={99}
            name="chevron-left"
            size="md"
          />{" "}
          {previousPageLabel}
        </button>
        {/* Page Number Input */}
        <Input
          id={inputId}
          type="text"
          value={currentPage || ""}
          min={1}
          max={totalPages}
          onChange={(e)=> handlePageChange(e, totalPages)}
          onBlur={(e) => {
            if (e.target.value === "") {
              setCurrentPage(1);
              onPageChange(1);
            }
          }}
          autoComplete="off"
          aria-label="Page"
          style={{ width: "4.2ch", paddingBottom: "0px", paddingTop: "0px" }}
        />
        {/* Page Indicator */}
        <span>
          {ofText} &nbsp;&nbsp; {totalPages}
        </span>
        {/* Next Button (Always Visible) */}
        <button
          onClick={goToNextPage}
          style={{
            visibility: currentPage === totalPages || currentPage === 0 ? "hidden" : "visible",
            display: "flex",
            marginTop: "5px",
            fontWeight: 600,
          }}
        >
          {nextPageLabel}
          <div style={{ display: "inlineBlock", verticalAlign: "top" }}>
            <Icon
              className=""
              counterLabel="Messages"
              counterValue={99}
              name="chevron-right"
              size="md"
            />
          </div>
        </button>
      </div>
    </div>
  );
};
